import { DateInt8, sameId, ValueRange } from "@cawita/core-front";
import { Stay, Partner } from "@shared/models";

export function getActiveStayForPartner(stays: Stay[], partnerId: string) {
  if (!stays?.length) return null;
  if (partnerId) {
    const stayIndex = stays?.findIndex(s => sameId(s.camping, partnerId));
    if (stayIndex > -1) return stays[stayIndex];
    return null;
  }
  return stays[stays?.length - 1];
}

export function getActiveStayForStay(stays: Stay[], stayId: string) {
  if (!stays?.length) return null;
  if (stayId) {
    const stayIndex = stays?.findIndex(s => sameId(s, stayId));
    if (stayIndex > - 1) return stays[stayIndex];
    return null;
  }
  return stays[stays?.length - 1];
}

export function filterOutStay(stays: Stay[], stay: Stay) {
  if (!stays?.length) return [];
  if (!stay) return stays;
  return stays.filter(s => !sameId(s, stay));
}

export interface StayMergeResult {
  shouldMerge: boolean;
  staysToMerge: (Stay | Partial<Stay>)[];
  staysOverlapping: (Stay | Partial<Stay>)[];
  mergeRange?: ValueRange<DateInt8>; // Only if shouldMerge = true
}

export function computeStayMerge(
  stay: Stay | Partial<Stay>,
  stays: (Stay | Partial<Stay>)[]
): StayMergeResult {
  const overlaps = getOverlappingStays(stay, stays);
  const samePartnerOverlaps = getSamePartnerStays(overlaps, stay.camping);
  if (samePartnerOverlaps?.length) {
    return {
      shouldMerge: true,
      staysOverlapping: overlaps,
      staysToMerge: samePartnerOverlaps,
      mergeRange: getMergedStayDateRange(stay, samePartnerOverlaps)
    }
  }

  return {
    shouldMerge: false,
    staysOverlapping: overlaps,
    staysToMerge: [],
  };
}

function getMergedStayDateRange(
  stay: Stay | Partial<Stay>,
  overlapping: (Stay | Partial<Stay>)[]
): ValueRange<DateInt8> {
  const range: ValueRange<DateInt8> = { start: stay.startDate, end: stay.endDate };
  overlapping.forEach((stay: Stay) => {
    if (range.start > stay.startDate) range.start = stay.startDate;
    if (range.end < stay.endDate) range.end = stay.endDate;
  });
  return range;
}


function getSamePartnerStays(
  stays: (Stay | Partial<Stay>)[],
  partner: Partner | Partial<Partner>
) {
  return stays.filter(s => sameId(s.camping, partner));
}

function getOverlappingStays(
  stay: Stay | Partial<Stay>,
  stays: (Stay | Partial<Stay>)[]
): (Stay | Partial<Stay>)[] {
  if (!stays?.length) return [];
  const otherStays = stays.filter(s => !sameId(stay, s)); // All stays without checked stay
  return otherStays.filter(s => areStaysOverlapping(stay, s));
}

function areStaysOverlapping(
  stayA: Stay | Partial<Stay>,
  stayB: Stay | Partial<Stay>
) {
  if (stayB.startDate > stayA.endDate) return false;
  if (stayB.endDate < stayA.startDate) return false;
  return true;
}
